/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import { type LogType, type Subscriber, type SubscriberUpdate } from '@common/types/api';
import { type InstallationId } from '@common/types/brandedTypes';
import { type ContractType } from '@common/types/models/Contract';
import type DeviceFull from '@common/types/models/Device/DeviceFull';
import type DeviceType from '@common/types/models/DeviceType';
import type TimeZone from '@common/types/models/TimeZone';
import type User from '@common/types/models/User';
import { type DeviceRegistrationFlowType, type PageLayout, UserState } from '@common/types/store';
import dstLessIanaId from '@common/utils/datetime/dstLessIanaId';
import { mobileAppInitialPath } from '@common/utils/device/mobileAppConstants';
import { type QueryData } from '@common/utils/formatting/usePagedViewQuery';

export interface MainStoreState {
  pageLayout: PageLayout;
  queryData?: QueryData;
  exportableDeviceCount?: number;
  userState: UserState;
  user?: User;
  contractTypes: ContractType[];
  deviceTypes?: DeviceType[];
  logTypes: LogType[];
  deviceDetails: Record<InstallationId, DeviceFull>;
  timeZones: Record<string, TimeZone>;
  languages: Record<string, string>;
  mobileUserMode?: 'full' | 'headless';
  deviceRegistrationType?: DeviceRegistrationFlowType;
}

let mobileUserMode: MainStoreState['mobileUserMode'] = undefined;
const openedFromMobile = window.location.pathname.toLowerCase() === `/${mobileAppInitialPath}`;
if (openedFromMobile) {
  const searchParams = new URLSearchParams(window.location.search);
  mobileUserMode = searchParams.get('mode')?.toLocaleLowerCase() === 'headless'
    ? 'headless'
    : 'full';
}

const initialState: MainStoreState = {
  mobileUserMode,
  pageLayout: 'regular',
  userState: UserState.Unknown,
  contractTypes: [],
  logTypes: [],
  deviceDetails: {},
  timeZones: {},
  languages: {},
};

const slice = createSlice({
  initialState,
  name: 'main',
  reducers: {
    setPageLayoutAction(state, action: PayloadAction<PageLayout>) {
      const pageLayout = action.payload;
      if (state.pageLayout !== pageLayout) {
        state.pageLayout = action.payload;
      }
    },
    setDeviceRegistrationTypeAction(
      state,
      action: PayloadAction<DeviceRegistrationFlowType | undefined>,
    ) {
      const registrationType = action.payload;
      if (state.deviceRegistrationType !== registrationType) {
        state.deviceRegistrationType = registrationType;
      }
    },
    setQueryDataAction(state, action: PayloadAction<QueryData>) {
      const queryData = action.payload;
      if (!isEqual(state.queryData, queryData)) {
        state.queryData = queryData;
      }
    },
    setExportableDeviceCountAction(state, action: PayloadAction<number>) {
      state.exportableDeviceCount = action.payload;
    },
    setUserStateAction(state, action: PayloadAction<UserState>) {
      state.userState = action.payload;
    },
    setUserAction(state, action: PayloadAction<User>) {
      state.userState = UserState.LoggedIn;
      state.user = {
        ...action.payload,
        ianaId: state.timeZones?.[action.payload.timeZoneId]?.ianaId,
      };
    },
    removeUserAction(state) {
      return {
        ...initialState,
        languages: state.languages,
        timeZones: state.timeZones,
        userState: UserState.LoggedOut,
      };
    },
    setContractTypesAction(state, action: PayloadAction<ContractType[]>) {
      state.contractTypes = action.payload;
    },
    setDeviceTypesAction(state, action: PayloadAction<DeviceType[]>) {
      state.deviceTypes = action.payload;
    },
    setLogTypesAction(state, action: PayloadAction<LogType[]>) {
      state.logTypes = action.payload;
    },
    addDeviceDetailsAction(
      state,
      action: PayloadAction<Pick<DeviceFull, 'id'> & Partial<DeviceFull>>,
    ) {
      const device = action.payload;
      const deviceFromState = state.deviceDetails[device.id] || {};

      if (device.timeZoneId) {
        const ianaId = state.timeZones?.[device.timeZoneId ?? deviceFromState.timeZoneId]?.ianaId;

        state.deviceDetails[device.id] = {
          ...deviceFromState,
          ...device,
          ianaId,
          dstLessIanaId: dstLessIanaId(ianaId),
        };
      } else {
        state.deviceDetails[device.id] = {
          ...deviceFromState,
          ...device,
        };
      }
    },
    addNotificationContactAction(
      state,
      action: PayloadAction<{
        installationId: InstallationId;
        subscriber: Subscriber;
      }>,
    ) {
      const { installationId, subscriber } = action.payload;
      if (!state.deviceDetails[installationId].notificationContacts) {
        state.deviceDetails[installationId].notificationContacts = [];
      }

      state.deviceDetails[installationId].notificationContacts.push(subscriber);
    },
    updateNotificationContactAction(
      state,
      action: PayloadAction<{
        installationId: InstallationId;
        subscriber: SubscriberUpdate;
      }>,
    ) {
      const { installationId, subscriber } = action.payload;
      const notificationContacts = state.deviceDetails[installationId].notificationContacts ?? [];
      const subscriberIndex = notificationContacts.findIndex(
        (contact) => contact.id === subscriber.id,
      );
      if (subscriberIndex !== -1) {
        const oldSubscriber = notificationContacts[subscriberIndex];
        state.deviceDetails[installationId].notificationContacts![subscriberIndex] = {
          ...oldSubscriber,
          ...subscriber,
        };
      }
    },
    deleteNotificationContactAction(
      state,
      action: PayloadAction<{
        installationId: InstallationId;
        notificationContactId: number;
      }>,
    ) {
      const { installationId, notificationContactId } = action.payload;
      const notificationContacts = state.deviceDetails[installationId].notificationContacts ?? [];
      state.deviceDetails[installationId].notificationContacts = notificationContacts.filter(
        (contact) => contact.id !== notificationContactId,
      );
    },
    setTimeZonesAction(state, action: PayloadAction<TimeZone[]>) {
      state.timeZones = action.payload.reduce(
        (result, timeZone) => ({
          ...result,
          [timeZone.id]: timeZone,
        }),
        {},
      );
    },
    setLanguagesAction(state, action: PayloadAction<Record<string, string>>) {
      state.languages = action.payload;
    },
  },
});

export const {
  setPageLayoutAction,
  setDeviceRegistrationTypeAction,
  setQueryDataAction,
  setExportableDeviceCountAction,
  setUserStateAction,
  setUserAction,
  removeUserAction,
  setContractTypesAction,
  setDeviceTypesAction,
  setLogTypesAction,
  addDeviceDetailsAction,
  addNotificationContactAction,
  updateNotificationContactAction,
  deleteNotificationContactAction,
  setTimeZonesAction,
  setLanguagesAction,
} = slice.actions;

export default slice.reducer;
